/**
 * PremiumReportCards — Renders the engine_output (13-step deterministic
 * Parashari analysis) as a premium card layout.
 *
 * Mandate (Acharya Guruji, Feb 2026):
 *   - "Backend ki ganana customer nahi dekhta — sirf experience dekhta hai."
 *   - Saari deep analysis ko premium card format mein dikhana hai.
 *   - Mobile-first, Gold + Navy theme, expand/collapse sections, fast load.
 *
 * Renders 8 cards (only when engine_output is present):
 *   1. Final Verdict Card (verdict + probability + key factors)
 *   2. Most Favourable Time Card (overall_strongest highlighted)
 *   3. Timing Windows Section (3 sub-cards 3-6 / 6-12 / 12-24 months)
 *   4. Mahadasha Card
 *   5. Antardasha Card
 *   6. Pratyantardasha Card
 *   7. Gochar Card (dasha lord transit + transit support)
 *   8. Upay Card (link to /upay)
 *
 * IMPORTANT: This is a pure presentational component. No backend calls,
 * no recomputation — it ONLY narrates engine_output values.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck, CalendarDays, Sparkles, Star, Globe,
  ChevronDown, Award,
} from 'lucide-react';

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay },
});

// Strength label → number of stars (visual)
const STARS = {
  'अति प्रबल': 5, 'Very Strong': 5,
  'प्रबल': 4, 'Strong': 4,
  'मध्यम': 3, 'Moderate': 3,
  'क्षीण': 2, 'Weak': 2,
};

// Verdict code → tone class
const VERDICT_TONE = {
  STRONGLY_SUPPORTIVE: { ring: 'border-emerald-400/60', glow: 'from-emerald-500/15', label: 'text-emerald-300' },
  MODERATELY_SUPPORTIVE: { ring: 'border-gold/60', glow: 'from-gold/15', label: 'text-gold' },
  MIXED: { ring: 'border-amber-400/50', glow: 'from-amber-500/15', label: 'text-amber-300' },
  WEAK_SUPPORT: { ring: 'border-orange-400/50', glow: 'from-orange-500/15', label: 'text-orange-300' },
  NOT_RECOMMENDED: { ring: 'border-rose-400/50', glow: 'from-rose-500/15', label: 'text-rose-300' },
};

const Stars = ({ count = 0 }) => (
  <span className="text-gold tracking-[0.15em]" aria-label={`${count} of 5`}>
    {'⭐'.repeat(count)}<span className="opacity-25">{'⭐'.repeat(5 - count)}</span>
  </span>
);

const SectionTitle = ({ icon, children, badge }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-gold/85 font-bold flex items-center gap-2">
      <span>{icon}</span>{children}
    </h3>
    {badge && <span className="text-[10px] text-text-muted/70">{badge}</span>}
  </div>
);

// ─────── Dasha Card (shared by MD / AD / PD) ───────
const DashaCard = ({ icon, title, block, transitBlock, delay = 0 }) => {
  if (!block || block.present === false) return null;
  const score = block.strength_score || 0;
  const verdict = block.strength_verdict_hi || '—';
  const supports = block.supports_question?.supports;
  return (
    <motion.div {...fadeIn(delay)}
      className="rounded-2xl bg-white/[0.025] border border-gold/20 p-4 sm:p-5"
      data-testid={`prem-card-${title}`}>
      <SectionTitle icon={icon} badge={block.label || title}>{title}</SectionTitle>
      <div className="flex flex-wrap items-baseline gap-3 mb-3">
        <span className="font-heading text-xl sm:text-2xl text-gold-gradient">
          {block.lord_hi || block.lord || '—'}
        </span>
        <span className="text-xs text-text-muted">
          {block.sign_hi || block.sign} {block.house ? `· भाव ${block.house}` : ''}
          {block.retrograde ? ' · वक्री' : ''}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px] sm:text-[13px] text-text-main mb-2.5">
        <div>शक्ति: <span className="text-gold font-semibold">{score}/100</span></div>
        <div>{verdict}</div>
        {block.dignity_hi && <div>स्थिति: {block.dignity_hi}</div>}
        {block.nakshatra && <div>नक्षत्र: {block.nakshatra.split(' / ')[1] || block.nakshatra}</div>}
        {block.pada && <div>पाद: {block.pada} ({block.pada_lord_hi})</div>}
        {block.start && <div className="col-span-2 text-text-muted/70 text-[11px]">
          {String(block.start).slice(0,10)} → {String(block.end).slice(0,10)}
        </div>}
      </div>
      {supports !== undefined && (
        <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full
          ${supports ? 'bg-emerald-500/12 text-emerald-300' : 'bg-rose-500/12 text-rose-300'}`}>
          {supports ? '✓ प्रश्न का समर्थन' : '✗ सीमित समर्थन'}
        </div>
      )}
      {block.supports_question?.reasons?.length > 0 && (
        <ul className="mt-3 space-y-1 text-[12px] text-text-muted">
          {block.supports_question.reasons.slice(0,3).map((r, i) =>
            <li key={i} className="flex gap-1.5">• <span>{r}</span></li>)}
        </ul>
      )}
      {/* Transit-of-this-lord layer */}
      {transitBlock?.present && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-[11px] text-gold/80 hover:text-gold flex items-center gap-1 select-none">
            <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
            वर्तमान गोचर स्थिति
          </summary>
          <div className="mt-2 text-[12px] text-text-main space-y-1 pl-4 border-l border-gold/20">
            <div>राशि: <b>{transitBlock.current_rashi_hi || transitBlock.current_rashi}</b> · भाव {transitBlock.transit_house}</div>
            {transitBlock.nakshatra && <div>नक्षत्र: {transitBlock.nakshatra.split(' / ')[1] || transitBlock.nakshatra} · पाद {transitBlock.pada}</div>}
            {transitBlock.drishti_on_houses?.length > 0 && <div>दृष्टि भाव: {transitBlock.drishti_on_houses.join(', ')}</div>}
            {transitBlock.yuti?.length > 0 && <div>युति: {transitBlock.yuti.join(', ')}</div>}
            {/* influence_summary_hi may already contain "दृष्टि भाव" / "युति" lines (backend) — filter to avoid duplication */}
            {(transitBlock.influence_summary_hi || [])
              .filter(s => !/^\s*(दृष्टि\s*भाव|युति)\s*[:：]/.test(s))
              .slice(0, 2)
              .map((s, i) => <div key={i} className="text-text-muted">• {s}</div>)}
          </div>
        </details>
      )}
    </motion.div>
  );
};

// ─────── Timing Window Sub-Card ───────
const WindowCard = ({ w, isOverallStrongest, delay = 0 }) => {
  const s = w.strongest;
  return (
    <motion.div {...fadeIn(delay)}
      className={`rounded-2xl p-4 sm:p-5 transition
        ${isOverallStrongest
          ? 'bg-gradient-to-br from-amber-900/40 via-gold/10 to-purple-900/30 border-2 border-gold shadow-[0_0_24px_rgba(255,215,0,0.15)]'
          : 'bg-white/[0.025] border border-gold/15'}`}
      data-testid={`window-card-${w.label}`}>
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-gold/80 font-bold">{w.label_hi}</p>
        {isOverallStrongest && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold text-purple-950 font-bold">सबसे प्रबल</span>
        )}
      </div>
      {s ? (
        <>
          <div className="flex items-baseline justify-between mb-2">
            <span className="font-heading text-lg sm:text-xl text-gold-gradient">
              {fmtDateHi(s.start)} – {fmtDateHi(s.end)}
            </span>
            <Stars count={STARS[s.strength_label_hi] || 3} />
          </div>
          <p className="text-[12px] text-text-muted mb-2">
            म.द. <b className="text-text-main">{s.md_lord_hi}</b>
            {' · '}अं.द. <b className="text-text-main">{s.ad_lord_hi}</b>
            {' · '}प्र.अं.द. <b className="text-text-main">{s.pd_lord_hi}</b>
          </p>
          <p className="text-[11px]"><span className="text-gold font-semibold">{s.strength_label_hi}</span></p>
          {s.reasons_hi?.length > 0 && (
            <ul className="mt-3 space-y-1 text-[12px] text-text-main/90">
              {s.reasons_hi.slice(0, 2).map((r, i) =>
                <li key={i} className="flex gap-1.5">• <span>{r}</span></li>)}
            </ul>
          )}
        </>
      ) : (
        <p className="text-[12px] text-text-muted italic">इस अवधि में कोई प्रबल योग नहीं।</p>
      )}
    </motion.div>
  );
};

function fmtDateHi(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const months = ['जनवरी', 'फ़रवरी', 'मार्च', 'अप्रैल', 'मई', 'जून',
                    'जुलाई', 'अगस्त', 'सितंबर', 'अक्तूबर', 'नवंबर', 'दिसंबर'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return String(iso).slice(0, 10); }
}

// ─────── Main exported component ───────
export default function PremiumReportCards({ engine_output, decision, vyavharik_salah }) {
  if (!engine_output) return null;
  const eo = engine_output;
  const verdict = eo.step13_verdict || {};
  const tone = VERDICT_TONE[verdict.verdict_code] || VERDICT_TONE.MIXED;
  const prob = eo.step12_probability || {};
  const timing = eo.step14_timing || {};
  const md = eo.step8_mahadasha;
  const ad = eo.step9_antardasha;
  const pd = eo.step10_pratyantardasha;
  const gochar = eo.step11_gochar || {};
  const transit = timing.dasha_lord_transit || {};

  return (
    <div className="space-y-5">
      {/* 1 — Final Verdict Card */}
      <motion.div {...fadeIn(0)}
        className={`relative overflow-hidden rounded-2xl border-2 ${tone.ring} bg-gradient-to-br ${tone.glow} to-[#0f0508]/40 p-5 sm:p-6`}
        data-testid="card-verdict">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gold/10 blur-[60px]" />
        <SectionTitle icon="🪔">अंतिम निष्कर्ष</SectionTitle>
        <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
          <div className="sm:col-span-2">
            <p className={`font-heading text-2xl sm:text-3xl ${tone.label}`}>
              {verdict.verdict_hi || decision?.verdict_hi}
            </p>
            <p className="text-xs text-text-muted mt-1">{verdict.verdict_en}</p>
            {decision?.key_factors?.length > 0 && (
              <ul className="mt-3 space-y-1 text-[12px] sm:text-sm text-text-main">
                {decision.key_factors.slice(0, 3).map((f, i) =>
                  <li key={i} className="flex gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" /><span>{f}</span></li>)}
              </ul>
            )}
          </div>
          <div className="rounded-xl bg-[#0f0508]/60 border border-gold/30 p-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80">सफलता संभावना</p>
            <p className="text-2xl sm:text-3xl font-heading text-gold-gradient leading-tight">
              {prob.label_hi || decision?.label_hi || '—'}
            </p>
            <p className="text-[10px] text-text-muted mt-1">शास्त्रीय आकलन</p>
          </div>
        </div>
      </motion.div>

      {/* 2 — Most Favourable Time Card */}
      {timing.overall_strongest && (
        <motion.div {...fadeIn(0.05)}
          className="rounded-2xl bg-gradient-to-br from-amber-900/35 via-gold/8 to-purple-900/25 border-2 border-gold p-5 sm:p-6 shadow-[0_0_28px_rgba(255,215,0,0.12)]"
          data-testid="card-favourable-time">
          <SectionTitle icon="🪔" badge="₹99 में शामिल">सर्वाधिक अनुकूल समय</SectionTitle>
          <div className="flex flex-wrap items-baseline gap-3 mb-3">
            <span className="font-heading text-2xl sm:text-3xl text-gold-gradient">
              <CalendarDays className="w-5 h-5 inline -mt-1 mr-1.5" />
              {fmtDateHi(timing.overall_strongest.start)} – {fmtDateHi(timing.overall_strongest.end)}
            </span>
            <Stars count={STARS[timing.overall_strongest.strength_label_hi] || 5} />
          </div>
          <p className="text-sm text-text-main mb-3">
            म.द. <b className="text-gold">{timing.overall_strongest.md_lord_hi}</b>
            {' · '}अं.द. <b className="text-gold">{timing.overall_strongest.ad_lord_hi}</b>
            {' · '}प्र.अं.द. <b className="text-gold">{timing.overall_strongest.pd_lord_hi}</b>
          </p>
          {timing.reasoning_hi?.length > 0 && (
            <div className="border-t border-gold/20 pt-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold/80 font-bold mb-2">यह समय क्यों अनुकूल है?</p>
              <ul className="space-y-1 text-[13px] text-text-main">
                {timing.reasoning_hi.slice(0, 4).map((r, i) =>
                  <li key={i} className="leading-relaxed">{r}</li>)}
              </ul>
            </div>
          )}
        </motion.div>
      )}

      {/* 3 — Three Timing Windows */}
      {timing.windows?.length > 0 && (
        <div data-testid="timing-windows-section">
          <SectionTitle icon="📅">तीन समय खिड़कियाँ</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {timing.windows.map((w, i) => (
              <WindowCard key={w.label} w={w} delay={0.06 * (i + 1)}
                isOverallStrongest={
                  !!timing.overall_strongest && w.strongest &&
                  w.strongest.start === timing.overall_strongest.start &&
                  w.strongest.pd_lord === timing.overall_strongest.pd_lord
                } />
            ))}
          </div>
        </div>
      )}

      {/* 4 — Mahadasha · 5 — Antardasha · 6 — Pratyantardasha */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DashaCard icon="🪐" title="महादशा" block={md} transitBlock={transit.mahadasha_lord} delay={0.05} />
        <DashaCard icon="🌙" title="अंतरदशा" block={ad} transitBlock={transit.antardasha_lord} delay={0.1} />
        <DashaCard icon="⭐" title="प्रत्यंतरदशा" block={pd} transitBlock={transit.pratyantardasha_lord} delay={0.15} />
      </div>

      {/* 7 — Gochar Card */}
      {gochar && (gochar.notes?.length || gochar.supports !== undefined) && (
        <motion.div {...fadeIn(0.1)}
          className="rounded-2xl bg-white/[0.025] border border-gold/20 p-5"
          data-testid="card-gochar">
          <SectionTitle icon={<Globe className="w-4 h-4 inline text-gold" />}>वर्तमान गोचर प्रभाव</SectionTitle>
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-[12px] px-3 py-1 rounded-full font-semibold
              ${gochar.supports ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
              {gochar.supports ? '✓ प्रश्न का समर्थन' : '△ मध्यम / मिश्रित'}
            </span>
          </div>
          {gochar.notes?.length > 0 && (
            <ul className="space-y-1.5 text-[13px] text-text-main">
              {gochar.notes.slice(0, 5).map((n, i) =>
                <li key={i} className="flex gap-1.5">• <span>{n}</span></li>)}
            </ul>
          )}
        </motion.div>
      )}

      {/* 8 — Upay Card (kept as soft link, integrates with the existing /upay page) */}
      {vyavharik_salah && (
        <motion.div {...fadeIn(0.15)}
          className="rounded-2xl bg-gradient-to-br from-gold/12 to-amber-500/5 border border-gold/40 p-5"
          data-testid="card-upay">
          <SectionTitle icon="🪔" badge="आज ही करें">अनुशंसित उपाय</SectionTitle>
          <p className="text-text-main text-[14px] sm:text-[15px] leading-[1.85] whitespace-pre-line">{vyavharik_salah}</p>
          <a href="/upay"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/50 hover:bg-gold/10 text-gold text-sm font-medium transition-colors"
            data-testid="card-upay-link">
            <Award className="w-4 h-4" /> विस्तृत नवग्रह उपाय पृष्ठ →
          </a>
        </motion.div>
      )}
    </div>
  );
}
